import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiUser, FiLoader, FiEdit2, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { customersAPI } from '../../services/api';

const AddCustomerModal = ({ isOpen, onClose, onCustomerSaved, customer = null }) => {
  const isEditMode = Boolean(customer?.id);

  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    gst_number: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // GST lookup state
  const [gstLookupStatus, setGstLookupStatus] = useState('idle'); // 'idle', 'loading', 'found', 'not_found', 'invalid'
  const [gstLookupTimeout, setGstLookupTimeout] = useState(null);
  const [matchedCustomerId, setMatchedCustomerId] = useState(null); // Store matched customer ID for update

  // Reset form when modal opens/closes or customer changes
  useEffect(() => {
    if (isOpen) {
      if (customer) {
        // Edit mode - pre-fill with customer data
        setFormData({
          name: customer.name || '',
          company_name: customer.company_name || '',
          email: customer.email || '',
          phone: customer.phone || '',
          gst_number: customer.gst_number || '',
          address: customer.address || '',
          city: customer.city || '',
          state: customer.state || '',
          pincode: customer.pincode || ''
        });
        setMatchedCustomerId(customer.id);
        setGstLookupStatus(customer.gst_number ? 'found' : 'idle');
      } else {
        // Add mode - reset form
        setFormData({
          name: '',
          company_name: '',
          email: '',
          phone: '',
          gst_number: '',
          address: '',
          city: '',
          state: '',
          pincode: ''
        });
        setMatchedCustomerId(null);
        setGstLookupStatus('idle');
      }
      setErrors({});
    }
  }, [isOpen, customer]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (gstLookupTimeout) {
        clearTimeout(gstLookupTimeout);
      }
    };
  }, [gstLookupTimeout]);

  // GST Number validation regex
  const isValidGSTIN = (gstin) => {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstinRegex.test(gstin);
  };

  // GST Lookup function
  const lookupGSTIN = useCallback(async (gstin) => {
    if (!gstin || gstin.length < 15) {
      setGstLookupStatus('idle');
      setMatchedCustomerId(null);
      return;
    }

    const cleanGstin = gstin.toUpperCase().trim();

    if (!isValidGSTIN(cleanGstin)) {
      setGstLookupStatus('invalid');
      setMatchedCustomerId(null);
      setErrors(prev => ({ ...prev, gst_number: 'Invalid GSTIN format' }));
      return;
    }

    // Clear GST error if valid format
    setErrors(prev => ({ ...prev, gst_number: '' }));
    setGstLookupStatus('loading');

    try {
      const response = await customersAPI.lookupGstin(cleanGstin);

      if (response.data.status === 'success' && response.data.data) {
        if (response.data.exists) {
          // Existing customer found - auto-fill all details
          const existingCustomer = response.data.data;

          // In edit mode, only allow if it's the same customer
          if (isEditMode && existingCustomer.id !== customer.id) {
            setGstLookupStatus('invalid');
            setErrors(prev => ({ ...prev, gst_number: 'GSTIN already exists for another customer' }));
            return;
          }

          setFormData({
            name: existingCustomer.name || '',
            company_name: existingCustomer.company_name || '',
            email: existingCustomer.email || '',
            phone: existingCustomer.phone || '',
            gst_number: cleanGstin,
            address: existingCustomer.address || '',
            city: existingCustomer.city || '',
            state: existingCustomer.state || '',
            pincode: existingCustomer.pincode || ''
          });
          setMatchedCustomerId(existingCustomer.id);
          setGstLookupStatus('found');
          toast.success('Customer found! Details auto-filled. Changes will update this customer.');
        } else {
          // New GST - just auto-fill state from GST code
          setFormData(prev => ({
            ...prev,
            gst_number: cleanGstin,
            state: response.data.data.state || prev.state
          }));
          setMatchedCustomerId(null);
          setGstLookupStatus('not_found');
        }
      } else {
        setGstLookupStatus('not_found');
        setMatchedCustomerId(null);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setGstLookupStatus('not_found');
        setMatchedCustomerId(null);
      } else {
        console.error('GST lookup error:', error);
        setGstLookupStatus('idle');
        setMatchedCustomerId(null);
      }
    }
  }, [isEditMode, customer]);

  // Handle GST input change with debounce
  const handleGstChange = (e) => {
    const value = e.target.value.toUpperCase();
    setFormData(prev => ({ ...prev, gst_number: value }));
    setGstLookupStatus('idle');
    setMatchedCustomerId(null);

    // Clear error when user starts typing
    if (errors.gst_number) {
      setErrors(prev => ({ ...prev, gst_number: '' }));
    }

    // Clear existing timeout
    if (gstLookupTimeout) {
      clearTimeout(gstLookupTimeout);
    }

    // Set new timeout for lookup (debounce 500ms)
    if (value.length >= 15) {
      const timeout = setTimeout(() => {
        lookupGSTIN(value);
      }, 500);
      setGstLookupTimeout(timeout);
    }
  };

  // Handle GST paste - immediate lookup
  const handleGstPaste = (e) => {
    setTimeout(() => {
      const value = e.target.value.toUpperCase();
      if (value.length >= 15) {
        lookupGSTIN(value);
      }
    }, 100);
  };

  // GST status indicator
  const getGstStatusIndicator = () => {
    switch (gstLookupStatus) {
      case 'loading':
        return <FiLoader className="animate-spin text-blue-500" size={18} />;
      case 'found':
        return <FiCheck className="text-green-500" size={18} />;
      case 'not_found':
        return <span className="text-xs text-orange-600 whitespace-nowrap">New Customer</span>;
      case 'invalid':
        return <FiAlertCircle className="text-red-500" size={18} />;
      default:
        return null;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.phone && !/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone should be 10 digits';
    }

    // Don't allow submit if GST is invalid
    if (gstLookupStatus === 'invalid') {
      newErrors.gst_number = 'Please fix the GSTIN error';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let response;

      // Determine if we should update or create
      // Update if: edit mode OR GST matched existing customer
      const shouldUpdate = isEditMode || matchedCustomerId;
      const customerId = isEditMode ? customer.id : matchedCustomerId;

      if (shouldUpdate && customerId) {
        // Update existing customer
        response = await customersAPI.update(customerId, formData);
        if (response.data.status === 'success') {
          toast.success('Customer updated successfully');
          onCustomerSaved({ ...formData, id: customerId });
          onClose();
        }
      } else {
        // Create new customer
        response = await customersAPI.create(formData);
        if (response.data.status === 'success') {
          toast.success('Customer created successfully');
          onCustomerSaved(response.data.data);
          onClose();
        }
      }
    } catch (error) {
      console.error('Save customer error:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isEditMode || matchedCustomerId ? 'bg-orange-100' : 'bg-blue-100'}`}>
              {isEditMode || matchedCustomerId ? (
                <FiEdit2 className="text-orange-600" size={20} />
              ) : (
                <FiUser className="text-blue-600" size={20} />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {isEditMode ? 'Edit Customer' : matchedCustomerId ? 'Update Existing Customer' : 'Add New Customer'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditMode ? 'Update customer details' : matchedCustomerId ? 'GSTIN matched - updating existing customer' : 'Enter customer details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* GSTIN Field with Auto-Lookup */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GSTIN
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="gst_number"
                  value={formData.gst_number}
                  onChange={handleGstChange}
                  onPaste={handleGstPaste}
                  placeholder="Enter GSTIN (auto-fills if customer exists)"
                  maxLength={15}
                  className={`w-full px-3 py-2 pr-24 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase ${
                    errors.gst_number ? 'border-red-500' :
                    gstLookupStatus === 'found' ? 'border-green-500 bg-green-50' :
                    'border-gray-300'
                  }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {getGstStatusIndicator()}
                </div>
              </div>
              {errors.gst_number && (
                <p className="mt-1 text-sm text-red-500">{errors.gst_number}</p>
              )}
              {gstLookupStatus === 'found' && !errors.gst_number && (
                <p className="mt-1 text-sm text-green-600">Customer found! Details loaded. Saving will update this customer.</p>
              )}
              {gstLookupStatus === 'not_found' && formData.gst_number.length >= 15 && (
                <p className="mt-1 text-sm text-orange-600">New GSTIN - A new customer will be created.</p>
              )}
            </div>

            {/* Customer Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter customer name"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                placeholder="Enter company name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="customer@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="2"
                placeholder="Enter address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* City, State, Pincode */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  placeholder="Enter pincode"
                  maxLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || gstLookupStatus === 'loading'}
              className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 ${
                isEditMode || matchedCustomerId ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin" size={16} />
                  {isEditMode || matchedCustomerId ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                isEditMode || matchedCustomerId ? 'Update Customer' : 'Save Customer'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerModal;
